import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// ---------------------------------------------------------------------------
// Rubro personalization for the home page.
// Every "demo" surface (hero inbox, problem cards, flow, system preview,
// audience) re-skins itself to the selected rubro so the visitor sees their
// own business instead of a generic one.
// ---------------------------------------------------------------------------

export type RubroId =
  | "aberturas"
  | "piletas"
  | "climatizacion"
  | "reformas"
  | "plomeria"
  | "electricidad"
  | "talleres"
  | "corralones"
  | "mayorista"
  | "inmobiliaria";

export type Rubro = {
  value: RubroId;
  label: string;
  biz: string;
  job: string;
  rec: string; // lo que toca reactivar
  price?: string;
};

export const RUBROS: Rubro[] = [
  {
    value: "aberturas",
    label: "Aberturas",
    biz: "Aberturas Sur",
    job: "Cambio de burletes",
    rec: "el mantenimiento anual",
  },
  {
    value: "piletas",
    label: "Piletas",
    biz: "Piletas & Piscinas",
    job: "Limpieza de pileta",
    rec: "el service de temporada",
  },
  {
    value: "climatizacion",
    label: "Climatización",
    biz: "Clima Hogar",
    job: "Instalación de split",
    rec: "la limpieza de filtros",
  },
  {
    value: "reformas",
    label: "Reformas",
    biz: "Reformas Integrales",
    job: "Refacción de baño",
    rec: "la revisión de obra",
  },
  {
    value: "plomeria",
    label: "Plomería",
    biz: "Plomería Express",
    job: "Destapación",
    rec: "la revisión de cañerías",
  },
  {
    value: "electricidad",
    label: "Electricidad",
    biz: "Eléctrica Pro",
    job: "Tablero nuevo",
    rec: "la revisión de tablero",
  },
  {
    value: "talleres",
    label: "Talleres mecánicos",
    biz: "BJB Repuestos",
    job: "Service de 10mil km",
    rec: "el próximo service",
  },
  {
    value: "corralones",
    label: "Corralones",
    biz: "Materiales Hoy",
    job: "Pedido de cemento",
    rec: "el pedido de la obra",
  },
  {
    value: "mayorista",
    label: "Mayoristas",
    biz: "TreeTecno",
    job: "Lista de precios",
    rec: "el lanzamiento de catálogo",
  },
  {
    value: "inmobiliaria",
    label: "Inmobiliaria",
    biz: "Hive Desarrollos",
    job: "Departamento en pozo",
    rec: "el lanzamiento de la torre",
  },
];

type RubroContextValue = {
  rbIndex: number;
  rb: Rubro;
  setRubro: (value: RubroId) => void;
  selectIndex: (i: number) => void;
};

const RubroContext = createContext<RubroContextValue | null>(null);

export function RubroProvider({ children }: { children: ReactNode }) {
  const [rbIndex, setRbIndex] = useState(0);

  const value = useMemo<RubroContextValue>(() => {
    const rb = RUBROS[rbIndex] ?? RUBROS[0];
    return {
      rbIndex,
      rb,
      setRubro: (value: RubroId) => {
        const i = RUBROS.findIndex((r) => r.value === value);
        if (i >= 0) setRbIndex(i);
      },
      selectIndex: (i: number) =>
        setRbIndex(Math.max(0, Math.min(RUBROS.length - 1, i))),
    };
  }, [rbIndex]);

  return (
    <RubroContext.Provider value={value}>{children}</RubroContext.Provider>
  );
}

export function useRubro() {
  const ctx = useContext(RubroContext);
  if (!ctx) throw new Error("useRubro must be used within RubroProvider");
  return ctx;
}

// Shared CTA helper — keeps analytics + navigation in one place.
export function agendar(label: string) {
  import("@/lib/analytics").then(({ trackCTA }) => trackCTA(label));
  if (typeof window !== "undefined") window.location.href = "/demo";
}
